import type { QueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import toast from "react-hot-toast";

import { getCertificateCheckout } from "@/services/certificatePaymentsService";
import {
  createMyAccountantRequest,
  uploadMyAccountantRequestDocument,
  type ConsumerCertificateRequestSource,
} from "@/services/userAccountantRequestsService";
import {
  createMyNotarialRequest,
  uploadMyNotarialRequestDocument,
  type UserNotarialRequestDocumentType,
} from "@/services/userNotarialRequestsService";
import {
  blobToFile,
  consumeCertificatePaymentResume,
  peekCertificatePaymentResume,
  type SerializedFileBlob,
} from "@/utils/certificatePaymentResume";

/**
 * Progress milestones emitted by the resume runner so the wizard can render an honest,
 * phased loading state ("confirming payment" → "creating request" → "uploading 3/8")
 * instead of a single opaque spinner.
 */
export type ResumeProgress =
  | { phase: "confirming_payment" }
  | { phase: "creating_request" }
  | { phase: "uploading_documents"; current: number; total: number }
  | { phase: "finalizing" };

export async function runCertificatePaymentResumeFlow(params: {
  checkoutId: string;
  cancelled: () => boolean;
  navigate: (to: string, options?: { replace?: boolean }) => void;
  onRequestCreated: (requestId: string, source: ConsumerCertificateRequestSource) => void;
  onProgress?: (progress: ResumeProgress) => void;
  /**
   * Called with the human-readable reason when creation fails.
   *
   * The runner's own signal is a toast, which suits the dLocal flow where the
   * consumer stays on a re-runnable URL. Callers that render a persistent
   * failure state need the reason to outlive the toast — otherwise the only
   * actionable information on screen ("you already have an active request")
   * disappears after a few seconds and the retry button looks arbitrary.
   */
  onCreateFailed?: (message: string) => void;
  queryClient: QueryClient;
  t: TFunction;
}): Promise<void> {
  const { checkoutId, cancelled, navigate, onRequestCreated, onProgress, onCreateFailed, queryClient, t } =
    params;
  onProgress?.({ phase: "confirming_payment" });
  try {
    const row = await getCertificateCheckout(checkoutId);
    if (cancelled()) return;
    // A bank-transfer checkout creates its request once the comprobante is
    // uploaded, while the money is still being verified — the request is held
    // server-side at pending_payment_verification, so nothing is exposed early.
    // Waiting for "paid" here would leave the consumer's answers and documents
    // stranded in IndexedDB for as long as a human review takes.
    const transferSubmitted =
      row.paymentMethod === "bank_transfer" && row.transferSubmittedAt != null;
    if (row.status !== "paid" && !transferSubmitted) {
      toast.error(t("requests.user.paymentPending"));
      navigate("/requests/new", { replace: true });
      return;
    }
    // Read-only fetch — the blob stays in IndexedDB until we've confirmed
    // the request was actually created server-side. If createRequest
    // throws after this we don't strand the user (the URL still has
    // `?certificate_payment=done&checkout_id=`, and a refresh re-runs
    // this flow from the same blob).
    const resume = await peekCertificatePaymentResume(checkoutId);
    if (cancelled()) return;
    if (!resume) {
      toast.error(t("requests.user.certificatePaymentResumeMissing"));
      navigate("/requests/new", { replace: true });
      return;
    }
    onProgress?.({ phase: "creating_request" });
    if (resume.kind === "simple_notary") {
      const created = await createMyNotarialRequest({
        requestType: resume.requestType,
        checkoutId: resume.checkoutId,
      });
      onProgress?.({ phase: "finalizing" });
      await queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
      // The "you have a confirmed payment that didn't finish" banner reads
      // this key, and the query sets refetchOnWindowFocus:false — so without
      // an explicit invalidation it keeps offering to resume a checkout that
      // is already consumed. Awaited before any navigate, per the project
      // rule on refreshing cache before reading or navigating.
      await queryClient.invalidateQueries({
        queryKey: ["me", "certificate-payments", "resumable"],
      });
      await queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests"] });
      await consumeCertificatePaymentResume(checkoutId);
      toast.success(t("requests.user.createSuccess"));
      onRequestCreated(created.id, "notarial");
      return;
    }
    if (resume.kind === "simple_accountant") {
      const created = await createMyAccountantRequest({
        type: resume.requestType,
        checkoutId: resume.checkoutId,
      });
      onProgress?.({ phase: "finalizing" });
      await queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
      // The "you have a confirmed payment that didn't finish" banner reads
      // this key, and the query sets refetchOnWindowFocus:false — so without
      // an explicit invalidation it keeps offering to resume a checkout that
      // is already consumed. Awaited before any navigate, per the project
      // rule on refreshing cache before reading or navigating.
      await queryClient.invalidateQueries({
        queryKey: ["me", "certificate-payments", "resumable"],
      });
      await queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests"] });
      await consumeCertificatePaymentResume(checkoutId);
      toast.success(t("requests.user.createSuccess"));
      onRequestCreated(created.id, "accountant");
      return;
    }
    if (resume.kind === "legal_entity") {
      const rf = resume.files;
      const created = await createMyNotarialRequest({
        requestType: "notarial_legal_entity",
        checkoutId: resume.checkoutId,
        fulfillmentMode: resume.fulfillmentMode,
        companySnapshot: {
          legalName: resume.validatedCompany.legalName,
          rut: resume.rut.replace(/\D/g, "").slice(0, 12),
          legalAddress: resume.validatedCompany.legalAddress,
          city: resume.validatedCompany.city,
          department: resume.validatedCompany.department,
          phone: resume.validatedCompany.phone,
          email: resume.validatedCompany.email,
        },
        invoiceIncludeRut: resume.invoiceIncludeRut === true,
        invoiceRutDigits:
          resume.invoiceIncludeRut === true ? resume.invoiceRutDigits ?? undefined : undefined,
        companyRutUnverified: resume.companyRutUnverified === true,
      });
      const legalEntityUploads: Array<[
        "estatuto" | "comunicacion_bcu" | "legal_document_front" | "legal_document_back" | "passport_document",
        Blob,
      ]> = [
        ["estatuto", rf.estatuto as unknown as Blob],
        ["comunicacion_bcu", rf.comunicacion_bcu as unknown as Blob],
      ];
      if (
        resume.legalDocumentType === "national_id" &&
        rf.legal_document_front &&
        rf.legal_document_back
      ) {
        legalEntityUploads.push(["legal_document_front", rf.legal_document_front as unknown as Blob]);
        legalEntityUploads.push(["legal_document_back", rf.legal_document_back as unknown as Blob]);
      } else if (rf.passport_document) {
        legalEntityUploads.push(["passport_document", rf.passport_document as unknown as Blob]);
      }
      let leUploaded = 0;
      onProgress?.({ phase: "uploading_documents", current: 0, total: legalEntityUploads.length });
      for (const [docType, blob] of legalEntityUploads) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await uploadMyNotarialRequestDocument(created.id, docType, blobToFile(blob as any));
        leUploaded += 1;
        onProgress?.({ phase: "uploading_documents", current: leUploaded, total: legalEntityUploads.length });
      }
      onProgress?.({ phase: "finalizing" });
      await queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
      // The "you have a confirmed payment that didn't finish" banner reads
      // this key, and the query sets refetchOnWindowFocus:false — so without
      // an explicit invalidation it keeps offering to resume a checkout that
      // is already consumed. Awaited before any navigate, per the project
      // rule on refreshing cache before reading or navigating.
      await queryClient.invalidateQueries({
        queryKey: ["me", "certificate-payments", "resumable"],
      });
      await queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests", created.id] });
      await consumeCertificatePaymentResume(checkoutId);
      toast.success(t("requests.user.createSuccess"));
      onRequestCreated(created.id, "notarial");
      return;
    }
    if (resume.kind !== "income_certificate") {
      toast.error(t("requests.user.certificatePaymentResumeMissing"));
      navigate("/requests/new", { replace: true });
      return;
    }
    const created = await createMyAccountantRequest({
      type: "income_certificate",
      checkoutId: resume.checkoutId,
      fulfillmentMode: resume.fulfillmentMode,
      incomeCertificateDetails: resume.incomeCertificateDetails,
      companySnapshot: resume.companySnapshot,
      invoiceIncludeRut: resume.invoiceIncludeRut === true,
      invoiceRutDigits:
        resume.invoiceIncludeRut === true ? resume.invoiceRutDigits ?? undefined : undefined,
      companyRutUnverified: resume.companyRutUnverified === true,
    });
    // Sequential uploads: parallel requests race on the per-type slot limit (income_invoice / salary receipt)
    // and can cause 400s after payment; company docs would succeed first while invoices fail.
    //
    // One failure must NOT abort the batch. Every file here is already paid for, so we push
    // through the whole list and report exactly which ones didn't land. Aborting on the first
    // rejection is what silently dropped 10 of one user's 22 invoices: they had no way to know
    // which files were missing, and re-uploading by hand misfiled them under other doc types.
    const total = resume.uploads.length;
    onProgress?.({ phase: "uploading_documents", current: 0, total });
    let uploaded = 0;
    const failedUploads: string[] = [];
    for (const u of resume.uploads) {
      try {
        await uploadResumeDocumentWithRetry(created.id, u);
      } catch (uploadErr) {
        console.error("[certificate-resume] upload failed:", u.name, uploadErr);
        failedUploads.push(u.name);
      }
      uploaded += 1;
      onProgress?.({ phase: "uploading_documents", current: uploaded, total });
    }
    onProgress?.({ phase: "finalizing" });
    await queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
    // The "you have a confirmed payment that didn't finish" banner reads
    // this key, and the query sets refetchOnWindowFocus:false — so without
    // an explicit invalidation it keeps offering to resume a checkout that
    // is already consumed. Awaited before any navigate, per the project
    // rule on refreshing cache before reading or navigating.
    await queryClient.invalidateQueries({
      queryKey: ["me", "certificate-payments", "resumable"],
    });
    await queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests"] });
    await queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests", created.id] });
    if (failedUploads.length > 0) {
      // Deliberately do NOT consume the blob: it still holds every file, so the
      // originals survive in IndexedDB for a support-side recovery. Naming the
      // failures lets the user re-upload them from the request detail page
      // instead of guessing which of 22 invoices is missing.
      toast.error(
        t("requests.user.certificateUploadsPartial", {
          count: failedUploads.length,
          files: failedUploads.join(", "),
        }),
      );
    } else {
      await consumeCertificatePaymentResume(checkoutId);
      toast.success(t("requests.user.createSuccess"));
    }
    onRequestCreated(created.id, "accountant");
  } catch (err) {
    // Checkout already consumed → a previous run created the request
    // server-side (uploads may have failed mid-way). Clear the stale
    // blob, surface a friendly toast, and send the user to their
    // requests list so they can find the parked request.
    if (isCheckoutAlreadyUsedError(err)) {
      console.warn("[certificate-resume] checkout already consumed, recovering:", err);
      await consumeCertificatePaymentResume(checkoutId);
      await queryClient.invalidateQueries({ queryKey: ["me", "certificate-requests"] });
      // The "you have a confirmed payment that didn't finish" banner reads
      // this key, and the query sets refetchOnWindowFocus:false — so without
      // an explicit invalidation it keeps offering to resume a checkout that
      // is already consumed. Awaited before any navigate, per the project
      // rule on refreshing cache before reading or navigating.
      await queryClient.invalidateQueries({
        queryKey: ["me", "certificate-payments", "resumable"],
      });
      await queryClient.invalidateQueries({ queryKey: ["me", "accountant-requests"] });
      await queryClient.invalidateQueries({ queryKey: ["me", "notarial-requests"] });
      toast.success(t("requests.user.createSuccess"));
      navigate("/requests", { replace: true });
      return;
    }
    // Signed in as someone other than the payer — the usual cause is a shared
    // device after the session was lost on the dLocal round-trip. Nothing was
    // created and nothing was charged again; the payment is still fine.
    //
    // Deliberately neither consumes the blob nor navigates: the runner peeks
    // until success, so leaving both alone means signing in as the account
    // that paid resumes from this same URL with the uploaded documents
    // intact. Clearing or redirecting here would turn a recoverable state
    // into a lost one.
    if (isCheckoutNotYoursError(err)) {
      console.warn("[certificate-resume] checkout belongs to another account:", err);
      toast.error(t("requests.user.certificatePaymentResumeWrongAccount"));
      return;
    }
    // The payment is already confirmed and the resume blob is still in
    // IndexedDB (we use peek, not consume, until success). Stay on the
    // current `/requests/new?certificate_payment=done&checkout_id=` URL
    // so a refresh re-runs the resume flow; do NOT dump the user back
    // to the empty wizard, which would strand them with a paid checkout
    // and no obvious recovery path.
    console.error("[certificate-resume] createRequest failed:", err);
    const serverMsg = extractServerMessage(err);
    const message = serverMsg
      ? `${t("requests.user.createError")} ${serverMsg}`
      : t("requests.user.createError");
    toast.error(message);
    onCreateFailed?.(message);
  }
}

/**
 * Upload one resumed document, retrying once on a transient failure.
 *
 * A 4xx is a deterministic rejection (bad mime type, slot limit) — retrying it
 * just burns the user's time on the post-payment spinner. Network drops and 5xx
 * are worth a second attempt, which is the common failure mode on the mobile
 * connections this flow runs on.
 */
async function uploadResumeDocumentWithRetry(
  requestId: string,
  upload: { docType: UserNotarialRequestDocumentType } & SerializedFileBlob,
): Promise<void> {
  try {
    await uploadMyAccountantRequestDocument(requestId, upload.docType, blobToFile(upload));
  } catch (err) {
    if (isDeterministicRejection(err)) throw err;
    await new Promise((resolve) => setTimeout(resolve, 800));
    await uploadMyAccountantRequestDocument(requestId, upload.docType, blobToFile(upload));
  }
}

function isDeterministicRejection(err: unknown): boolean {
  const status = (err as { response?: { status?: unknown } } | null)?.response?.status;
  if (typeof status !== "number") return false;
  // 408 (timeout) and 429 (rate limit) are 4xx but genuinely worth retrying.
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

function isCheckoutAlreadyUsedError(err: unknown): boolean {
  const msg = extractServerMessage(err);
  return typeof msg === "string" && msg.toLowerCase().includes("checkout was already used");
}

/**
 * Matched on the server's `errorCode`, not on its message.
 *
 * Deliberately not the string-matching shape of `isCheckoutAlreadyUsedError`
 * above: that one breaks the moment the message is reworded or translated,
 * and it fails SILENTLY into the generic branch when it does — which is the
 * failure this function exists to fix on a neighbouring path.
 */
function isCheckoutNotYoursError(err: unknown): boolean {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data as
    | { errorCode?: unknown }
    | undefined;
  return data?.errorCode === "checkout_not_yours";
}

/**
 * Pull a useful one-line error message off an axios / fetch error so the
 * user sees backend feedback (e.g. validation messages) instead of just
 * the generic "Intentá de nuevo" toast.
 */
function extractServerMessage(err: unknown): string | null {
  if (err == null || typeof err !== "object") return null;
  // axios-shaped: err.response.data.message
  const e = err as {
    response?: { data?: { message?: unknown } };
    message?: unknown;
  };
  const body = e.response?.data?.message;
  if (typeof body === "string" && body.length > 0) return body;
  if (Array.isArray(body) && body.length > 0 && typeof body[0] === "string") return body.join(". ");
  if (typeof e.message === "string" && e.message.length > 0 && !e.message.startsWith("Request failed")) {
    return e.message;
  }
  return null;
}
